// services/menuService.ts
import axios from '../utils/axios';

export interface MenuFilters {
  page?: number;
  limit?: number;
  status?: 'active' | 'inactive';
  search?: string;
}

export interface Menu {
  id: number;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  statistics: {
    categories: number;
    dishes: number;
    views: number;
    qrScans: number;
  };
  restaurant: {
    id: number;
    name: string;
    address: string;
    phone: string;
    logoUrl?: string;
  } | null;
  createdBy?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalMenus: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
}

export interface MenusResponse {
  success: boolean;
  message: string;
  data: {
    menus: Menu[];
    pagination: Pagination;
    userRole?: string;
  };
  error?: string;
}

export interface MenuResponse {
  success: boolean;
  message: string;
  data: Menu;
  error?: string;
}

export interface BaseResponse {
  success: boolean;
  message: string;
  error?: string;
}

export const menuService = {
  // Get all menus for current user
  getAllMenus: async (params?: MenuFilters): Promise<MenusResponse> => {
    try {
      const response = await axios.get('/menu/all', { params });
      return response.data;
    } catch (error: any) {
      console.error('Get all menus error:', error);
      return {
        success: false,
        message: 'Failed to fetch menus',
        error: error.response?.data?.error || error.message,
        data: {
          menus: [],
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalMenus: 0,
            hasNextPage: false,
            hasPrevPage: false,
            limit: params?.limit || 10
          }
        }
      };
    }
  },

  // Get single menu details
  getMenuById: async (menuId: string): Promise<MenuResponse> => {
    try {
      const response = await axios.get(`/menu/${menuId}`);
      return response.data;
    } catch (error: any) {
      console.error('Get menu by ID error:', error);
      return {
        success: false,
        message: 'Failed to fetch menu',
        error: error.response?.data?.error || error.message,
        data: {} as Menu
      };
    }
  },

  // Update menu
  updateMenu: async (menuId: string, data: any): Promise<BaseResponse> => {
    try {
      const response = await axios.put(`/menu/${menuId}`, data);
      return response.data;
    } catch (error: any) {
      console.error('Update menu error:', error);
      return {
        success: false,
        message: 'Failed to update menu',
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Delete menu (soft delete)
  deleteMenu: async (menuId: string): Promise<BaseResponse> => {
    try {
      const response = await axios.delete(`/menu/${menuId}`);
      return response.data;
    } catch (error: any) {
      console.error('Delete menu error:', error);
      return {
        success: false,
        message: 'Failed to delete menu',
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Create menu from Google
  createMenuFromGoogle: async (data: { placeId: string; theme?: string; background?: string }): Promise<BaseResponse> => {
    try {
      const response = await axios.post('/menu/create', data);
      return response.data;
    } catch (error: any) {
      console.error('Create menu from Google error:', error);
      return {
        success: false,
        message: 'Failed to create menu',
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Get WhatsApp numbers linked to a menu
  getMenuWhatsAppNumbers: async (menuId: string): Promise<any> => {
    try {
      const response = await axios.get(`/menu/${menuId}/whatsapp`);
      return response.data;
    } catch (error: any) {
      console.error('Get menu WhatsApp numbers error:', error);
      return {
        success: false,
        message: 'Failed to fetch WhatsApp numbers',
        error: error.response?.data?.error || error.message,
        data: []
      };
    }
  },

  // Search restaurant
  searchRestaurant: async (name: string): Promise<any> => {
    try {
      const response = await axios.post('/menu/search', { name });
      return response.data;
    } catch (error: any) {
      console.error('Search restaurant error:', error);
      return {
        success: false,
        message: 'Failed to search restaurant',
        error: error.response?.data?.error || error.message
      };
    }
  },

  // Fetch restaurant details
  fetchRestaurantDetails: async (placeId: string): Promise<any> => {
    try {
      const response = await axios.post('/menu/details', { placeId });
      return response.data;
    } catch (error: any) {
      console.error('Fetch restaurant details error:', error);
      return {
        success: false,
        message: 'Failed to fetch restaurant details',
        error: error.response?.data?.error || error.message
      };
    }
  }
};

// AI Menu Import interfaces
export interface ImportResponse {
  success: boolean;
  message: string;
  data?: any;
  stats?: {
    categories: number;
    items: number;
  };
  error?: string;
  suggestion?: string;
}

// AI Menu Import functions
export const aiMenuImportService = {
  // Import menu from file (PDF, DOC, DOCX)
  importFromFile: async (menuId: string, file: File): Promise<ImportResponse> => {
  try {
   

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ];
    
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      return {
        success: false,
        message: 'Unsupported file type',
        error: 'File type not supported. Please upload PDF, DOC, DOCX, TXT, or image files.',
        suggestion: 'Convert your file to a supported format or try a different file.'
      };
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        success: false,
        message: 'File too large',
        error: `File size exceeds 10MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`,
        suggestion: 'Compress your file or use a smaller file.'
      };
    }

    // Create FormData
    const formData = new FormData();
    formData.append('file', file);
    formData.append('menuId', menuId);
    
    // ES5 compatible FormData debugging
    const entries = formData.entries();
    let entry = entries.next();
    while (!entry.done) {
      const [key, value] = entry.value;
      console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
      entry = entries.next();
    }

    // Make the request with proper headers
    const response = await axios.post(`/menu/management/${menuId}/import/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes timeout for large files and AI processing
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      }
    });

    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data.trim());
      } catch (e) {
        console.warn('Failed to parse response data:', e);
      }
    }
    return data;
  } catch (error: any) {
    
    // Detailed error handling
    let errorMessage = 'Failed to import menu from file';
    let suggestion = 'Please ensure the file contains readable text and try again.';
    
    if (error.response) {
      // The request was made and the server responded with a status code
      console.error('Response error details:', error.response.data);
      errorMessage = error.response.data?.message || error.response.statusText || 'Server error';
      suggestion = error.response.data?.suggestion || suggestion;
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
      errorMessage = 'No response from server. Please check your connection.';
      suggestion = 'Check your internet connection and try again.';
    } else if (error.code === 'ECONNABORTED') {
      // Timeout error
      errorMessage = 'Request timeout. The file might be too large.';
      suggestion = 'Try with a smaller file or compress the file before uploading.';
    }
    
    return {
      success: false,
      message: errorMessage,
      error: error.response?.data?.error || error.message || 'Unknown error',
      suggestion: suggestion,
    };
  }
},

  // Import menu from text input
  importFromText: async (menuId: string, text: string): Promise<ImportResponse> => {
    try {
      // Validate text input
      if (!text.trim()) {
        return {
          success: false,
          message: 'Empty text input',
          error: 'Please enter menu text to import',
          suggestion: 'Paste your menu text in the text area and try again.'
        };
      }

      // Validate text length
      if (text.length > 50000) { // 50KB limit
        return {
          success: false,
          message: 'Text too long',
          error: `Text exceeds 50KB limit. Your text is ${(text.length / 1024).toFixed(2)}KB.`,
          suggestion: 'Please shorten your menu text or use file upload for larger menus.'
        };
      }

      const response = await axios.post(`/menu/management/${menuId}/import/text`, { text });
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data.trim());
        } catch (e) {
          console.warn('Failed to parse response data:', e);
        }
      }
      return data;
    } catch (error: any) {
      console.error('Import from text error:', error);
      
      let errorMessage = 'Failed to import menu from text';
      let suggestion = 'Please check your text format and try again.';
      
      if (error.response) {
        console.error('Response error details:', error.response.data);
        errorMessage = error.response.data?.message || error.response.statusText || 'Server error';
        suggestion = error.response.data?.suggestion || suggestion;
      } else if (error.request) {
        console.error('No response received:', error.request);
        errorMessage = 'No response from server. Please check your connection.';
        suggestion = 'Check your internet connection and try again.';
      }
      
      return {
        success: false,
        message: errorMessage,
        error: error.response?.data?.error || error.message || 'Unknown error',
        suggestion: suggestion,
      };
    }
  },

  // Import menu from URL
  importFromURL: async (menuId: string, url: string): Promise<ImportResponse> => {
    try {
      // Validate URL
      try {
        new URL(url);
      } catch {
        return {
          success: false,
          message: 'Invalid URL',
          error: 'Please provide a valid URL starting with http:// or https://',
          suggestion: 'Check the URL format and try again.'
        };
      }

      const response = await axios.post(`/menu/management/${menuId}/import/url`, { url });
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data.trim());
        } catch (e) {
          console.warn('Failed to parse response data:', e);
        }
      }
      return data;
    } catch (error: any) {
      console.error('Import from URL error:', error);
      
      let errorMessage = 'Failed to import menu from URL';
      let suggestion = 'Please try a different URL or use file upload instead.';
      
      if (error.response?.status === 404) {
        errorMessage = 'URL not found or inaccessible';
        suggestion = 'Check if the URL is correct and accessible.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access to URL forbidden';
        suggestion = 'The website might block automated access. Try downloading the file and uploading it directly.';
      }
      
      return {
        success: false,
        message: errorMessage,
        error: error.response?.data?.error || error.message,
        suggestion: error.response?.data?.suggestion || suggestion,
      };
    }
  },

  // Test Italian menu parsing
  testItalianMenuParsing: async (): Promise<ImportResponse> => {
    try {
      const response = await axios.post('/menu/management/test-italian-parsing');
      return response.data;
    } catch (error: any) {
      console.error('Test Italian menu parsing error:', error);
      return {
        success: false,
        message: 'Failed to test Italian menu parsing',
        error: error.response?.data?.error || error.message,
        suggestion: 'Please ensure OpenAI API key is configured and try again.',
      };
    }
  },

  // Get import status (optional - if your API supports it)
  getImportStatus: async (menuId: string, taskId: string): Promise<ImportResponse> => {
    try {
      const response = await axios.get(`/menu/management/${menuId}/import/status/${taskId}`);
      return response.data;
    } catch (error: any) {
      console.error('Get import status error:', error);
      return {
        success: false,
        message: 'Failed to get import status',
        error: error.response?.data?.error || error.message,
      };
    }
  }
};